import React, { useMemo } from "react";
import {
  BluetoothConnectedFill,
  BluetoothDisabledFill,
} from "@material-symbols-svg/react";

import CgmSyncButton from "./CgmSyncButton";
import Badge from "src/components/common/Badge";
import TimerDisplay from "../TimerDisplay";
import BatteryIndicator from "../BatteryIndicator";

import { useAppSelector } from "src/redux/hooks";
import useCgmHook from "src/hooks/useCgm.hook";
import { CGM_DISPLAY_STATUS_CONFIG, CGM_IN_RANGE } from "src/constants/cgm";

const CgmReadingCard: React.FC = () => {
  const { glucoseValue, timestamp, status, warmupCountdownMMSS } = useCgmHook();
  const cgm = useAppSelector((state) => state.cgm);
  const batteryPercent = cgm.dailyLogs.data?.batteryPercent ?? 100;

  const statusColors = CGM_DISPLAY_STATUS_CONFIG[status.code];

  const glucoseStatus = useMemo(() => {
    if (glucoseValue == null) return null;
    if (Number(glucoseValue) < CGM_IN_RANGE.MIN)
      return { label: "Low", type: "danger" as const };
    if (Number(glucoseValue) > CGM_IN_RANGE.MAX)
      return { label: "High", type: "warning" as const };
    return { label: "In Range", type: "success" as const };
  }, [glucoseValue]);

  return (
    <div className="relative bg-white p-4 rounded-lg shadow-sm border border-gray-100 grid grid-cols-2 gap-4">
      <p
        className="absolute top-4 left-4 text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
        style={{
          backgroundColor: statusColors.bgColor,
          color: statusColors.color,
        }}
      >
        {status.code === "WARMUP" && warmupCountdownMMSS
          ? `Warmup · ${warmupCountdownMMSS}`
          : status.label}
      </p>
      <div className="pt-6 grid-cols-1 flex flex-col items-start gap-1">
        <p className="text-2xl font-bold">
          {glucoseValue}{" "}
          <span className="text-sm text-gray-500 font-normal">mg/dl</span>
        </p>
        <p className="text-sm text-gray-500 -mt-0.5">
          Synced{" "}
          {timestamp ? (
            <TimerDisplay
              className="text-gray-600 font-semibold"
              timestamp={timestamp}
              showLiveUpdate={true}
            />
          ) : (
            "--:--"
          )}
        </p>
      </div>

      <div className="grid-cols-1 flex flex-col gap-2 items-end justify-between h-full">
        {status.code !== "WARMUP" && status.code !== "EXPIRED" ? (
          <div className="flex items-center gap-2">
            {status.code === "CONNECTED" && <CgmSyncButton />}
            {status.transmitterOk ? (
              <BluetoothConnectedFill className="w-4 h-4 text-green-600" />
            ) : (
              <BluetoothDisabledFill className="w-4 h-4 text-red-600" />
            )}

            {batteryPercent != null && (
              <BatteryIndicator batteryPercent={batteryPercent} />
            )}
          </div>
        ) : (
          <div></div>
        )}

        {status.code !== "WARMUP" && glucoseStatus && (
          <Badge type={glucoseStatus.type} className="">
            {glucoseStatus.label}
          </Badge>
        )}
      </div>
    </div>
  );
};

export default CgmReadingCard;
